import React from "react";
import { jsPDF } from "jspdf";
import autoTable, { type Styles } from "jspdf-autotable";
import * as QRCode from "qrcode";
import { formatDateTimeWithMonth, getMaxReportingDate } from "../../utils/dateTimeUtils";
import { useSelector} from 'react-redux';
import {
  drawNeqasLogoBesideVerification,
  fetchVerifiedByNeqas,
  loadNeqasLogo,
} from "../lab/reports/neqasFooterLogo";
                                                                                                                                                                                                                                                                            

// Define Types based on provided data structure
interface Patient {
  _id: string;
  patientName: string;
  guardiansName: string;
  gender: string;
  dob: string;
  phonNumber: string;
  mrn: any;
  ref_mrn?: string;
}
interface PatientType {
  patientTypeId: string;
  min: number;
  max: number;
  criticalMin: number;
  criticalMax: number;
  unit_cutOff: string;
  minMaxInWord?: string;
  belowMinValueText: string;
  belowMinValueColor: string;
  noramalRangeText: string;
  normalRangeText: string;
  noramalRangeColor: string;
  normalRangeColor: string;
  aboveMaxValueText: string;
  aboveMaxValueColor: string;
  parameterType?: string;
}
interface TestParameter {
  parameter: string;
  result: string | null;
  normalValues: string;
  units: string;
  comments?: string;
  isAbnormal: boolean;
  printOnReport: boolean;
  description?: string;
  patientType?: PatientType;
  interpretation?: string;
  patientValue?: string;
}
interface HistoryEntry {
  _id: string;
  testParameterToPerform: TestParameter[];
  createdAt: string;
  performedOn: string;
  approvedOn?: string;
}
interface CultureMedicine {
  medicineName: string;
  medicineGroup: string;
  sortOrder: number;
  sensitive_1: string;
  sensitive_2: string;
  sensitive_3: string;
}
interface TestResult {
  _id: string;
  testId: string;
  testName: string;
  displayName: string;
  testResultType: any;
  testResultTemplate?: string;
  testCode: number;
  charges: number;
  chemicalExamination?: {
    specimen: string;
    cultureGrowth: string;
  };
  testParameterToPerform: TestParameter[];
  resultValue: string;
  viralLoad: string;
  method: string;
  comments: string;
  notes: string;
  history: HistoryEntry[];
  cultureMedicines?: CultureMedicine[];
  createdAt: string;
  performedOn: string;
  approvedOn?: string;
  reportingTime?: string;
}
interface RoleInfo {
  name?: string;
  qualification?: string;
  designation?: string;
}

interface ReportData {
  createdAt: string;
  _id: string;
  projectId: string;
  patientId: string;
  maritalStatus: string;
  collectionDate: string;
  reportingDate: string;
  trNo: string;
  patient: Patient;
  testResults: TestResult[];
  collectionCenter?: {
    collectionCenterName?: string;
  };
  otherDetails: {
    referredBy?: string;
    specimenType?: string;
  };
  pageHeader?: {
    title?: string;
    address?: string;
    phone?: string;
    phone2?: string;
    email?: string;
    logo?: string;
    website?: string;
    PHC_REG_NO?: string;
    hospitalName?: string;
  };
  pageFooter?: {
    Pathologist?: RoleInfo;
    Microbiologist?: RoleInfo;
    Biochemist?: RoleInfo;
    Technologist?: RoleInfo;
    verificationText?: string;
  };
  createdBy?: {
    fullName?: string;
    firstName?: string;
  };
}
interface ReportPDFProps {
  logo?: string;
  print?: boolean;
  data?: ReportData;
  onClose?: () => void;
}
const calculateAge = (dob: string, referenceDate?: string): string => {
  const birthDate = new Date(dob);
  const today = referenceDate ? new Date(referenceDate) : new Date();
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  
  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Ensure non-negative values
  years = Math.max(0, years);
  months = Math.max(0, months);
  days = Math.max(0, days);
  
  // Return in format: 0y-0m-0d
  return `${years}y-${months}m-${days}d`;
};

const isWithinNormalRange = (
  result: string | null,
  normal: string,
  patientType?: PatientType
) => {
  if (!result || result === null)
    return { isHigh: false, isLow: false, status: "--" };
  const value = Number.parseFloat(result);
  if (isNaN(value)) return { isHigh: false, isLow: false, status: "--" };
  // Use patientType data if available
  if (patientType) {
    // No range defined (min and max both 0) — treat as normal, no arrow/highlight
    if (patientType.min === 0 && patientType.max === 0) {
      return { isHigh: false, isLow: false, status: patientType.noramalRangeText || "NORMAL" };
    }
    const isHigh = value > patientType.max;
    const isLow = value < patientType.min;
    let status = patientType.noramalRangeText || "NORMAL";
    if (isHigh) {
      status = patientType.aboveMaxValueText || "HIGH";
    } else if (isLow) {
      status = patientType.belowMinValueText || "LOW";
    }
    return { isHigh, isLow, status };
  }
  // Fallback to normal range parsing
  if (normal.includes("Above")) {
    const min = Number.parseFloat(normal.split("Above ")[1]);
    return {
      isHigh: value > min,
      isLow: false,
      status: value > min ? "HIGH" : "NORMAL",
    };
  } else if (normal.includes("-")) {
    const [min, max] = normal.split("-").map(v => Number.parseFloat(v.trim()));
    const isHigh = value > max;
    const isLow = value < min;
    return {
      isHigh,
      isLow,
      status: isHigh ? "HIGH" : isLow ? "LOW" : "NORMAL",
    };
  }
  return { isHigh: false, isLow: false, status: "NORMAL" };
};
const getResultColor = (
  patientType?: PatientType,
  isHigh?: boolean,
  isLow?: boolean
): [number, number, number] => {
  if (patientType) {
    if (isHigh && patientType.aboveMaxValueColor) {
      const color = patientType.aboveMaxValueColor;
      if (color.startsWith("#")) {
        const hex = color.substring(1);
        const r = Number.parseInt(hex.substr(0, 2), 16);
        const g = Number.parseInt(hex.substr(2, 2), 16);
        const b = Number.parseInt(hex.substr(4, 2), 16);
        return [r, g, b];
      }
    }
    if (isLow && patientType.belowMinValueColor) {
      const color = patientType.belowMinValueColor;
      if (color.startsWith("#")) {
        const hex = color.substring(1);
        const r = Number.parseInt(hex.substr(0, 2), 16);
        const g = Number.parseInt(hex.substr(2, 2), 16);
        const b = Number.parseInt(hex.substr(4, 2), 16);
        return [r, g, b];
      }
    }
    if (!isHigh && !isLow && patientType.noramalRangeColor) {
      const color = patientType.noramalRangeColor;
      if (color.startsWith("#")) {
        const hex = color.substring(1);
        const r = Number.parseInt(hex.substr(0, 2), 16);
        const g = Number.parseInt(hex.substr(2, 2), 16);
        const b = Number.parseInt(hex.substr(4, 2), 16);
        return [r, g, b];
      }
    }
  }
  // Fallback colors
  if (isHigh) return [220, 38, 38]; // Red for high
  if (isLow) return [255, 140, 0]; // Orange for low
  return [34, 197, 94]; // Green for normal
};
const parseHexColor = (hex: string): [number, number, number] => {
  if (!hex || !hex.startsWith("#")) return [0, 0, 0];
  const r = Number.parseInt(hex.substr(1, 2), 16);
  const g = Number.parseInt(hex.substr(3, 2), 16);
  const b = Number.parseInt(hex.substr(5, 2), 16);
  return [r, g, b];
};
const ReportPDF: React.FC<ReportPDFProps> = ({
  print = false,
  onClose,
  data,
}) => {
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );

  // useEffect(() => {
  //   if (!profile) {
  //     dispatch(fetchProfileData());
  //   }
  // }, [dispatch, profile]);
  
  const generatePDF = async (): Promise<void> => {
    
    if (!data) return;
    const doc = new jsPDF("p", "pt", "a4");
    const PAGE_HEIGHT = 780; // Set slightly below the autoTable bottom boundary (~722) to prevent internal splitting
    const MARGIN_LEFT = 20;
    const MARGIN_RIGHT = 20;
    // Helper function to draw label-value pairs
    const drawLabelValue = (
      doc: jsPDF,
      label: string,
      value: string | number | undefined,
      x: number,
      y: number,
      labelWidth: number = 0
    ) => {
      const labelStr = String(label ?? "");
      const valueStr = String(value ?? "--");
      doc.setFont("helvetica", "normal");
      doc.text(labelStr, x, y);
      doc.setFont("helvetica", "bold");
      // If labelWidth is provided, align value at x + labelWidth, otherwise use old behavior
      const valueX = labelWidth > 0 ? x + labelWidth : x + doc.getTextWidth(labelStr) + 2;
      doc.text(valueStr, valueX, y);
      doc.setFont("helvetica", "normal");
    };
    // Header and patient information
    const addHeaderAndPatientInfo = async () => {
      // --- Colors ---
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      // --- Logo on the left ---
      // Replace `logoBase64` with your base64-encoded logo image string
      const logoSize = 55;
      const logoX = 20;
      const logoY = 20;
      if(data?.pageHeader?.logo){
      doc.addImage(`${data?.pageHeader?.logo || ""}`, "PNG", logoX, logoY, logoSize, logoSize);
      }
      
      // --- QR Code on the right side of hospital header ---
      // Generate compact QR URL with format: ?tr=TRNumber_ProjectId
      const trNumber = data?.trNo || "";
      const projectId = data?.projectId || "";
      const qrUrl = `${process.env.REACT_APP_QR_BASE_URL}?tr=${trNumber}_${projectId}`;

      const qrSize = 60;
      const qrX = 520;
      const qrY = 12;

      try {
        const qrDataUrl = await QRCode.toDataURL(qrUrl);
        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, 60);

        // Text settings
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6);
        doc.setTextColor(0, 0, 0);
        const scanText = "Scan QR Code to download Report";

        // Wrap text into max 2 lines based on QR width
        const lines = doc
          .splitTextToSize(scanText, qrSize)
          .slice(0, 2);

        // Center text under QR
        const textX = qrX + qrSize / 2;
        const textY = qrY + 60;

        doc.text(lines, textX, textY, {
          align: "center",
        });
      } catch (error) {
        console.error("Error generating QR code:", error);
      }

      

      // --- PHC Reg No (top right, above title) ---
      if (data?.pageHeader?.PHC_REG_NO) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        const phcText = `PHC Reg No: ${data?.pageHeader?.PHC_REG_NO}`;
        const phcWidth = doc.getTextWidth(phcText);
        const phcX = qrX - 50 - phcWidth; // Align to left of QR with 10pt margin
        doc.text(phcText, phcX, 25);
      }

      // --- Main header (centered between logo and QR) ---
      const pageWidth = 595; // A4 width in points
      const logoEnd = logoX + logoSize;
      const availableWidth = qrX - logoEnd; // Space between logo end and QR start
      const centerX = logoEnd + (availableWidth / 2); // Center point between logo and QR
      
      if (data?.pageHeader?.title) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(0, 0, 0);
        const titleText = data.pageHeader.title;
        const titleWidth = doc.getTextWidth(titleText);
        const titleX = centerX - (titleWidth / 2);
        doc.text(titleText, titleX, 45);
      }
      // --- Subheader (centered between logo and QR) ---
      if (data?.pageHeader?.address) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        const addressText = data.pageHeader.address;
        const addressWidth = doc.getTextWidth(addressText);
        const addressX = centerX - (addressWidth / 2);
        doc.text(addressText, addressX, 62);
      }
      // --- Contact info line 1 (website - only if exists) ---
      let currentContactY = 72;
      // if (data?.pageHeader?.website) {
      //   doc.setFont("helvetica", "normal");
      //   doc.setFontSize(10);
      //   const websiteWidth = doc.getTextWidth(`${data?.pageHeader?.website}`);
      //   const websiteX = (pageWidth - websiteWidth) / 2 + 35;
      //   doc.text(`${data?.pageHeader?.website}`, websiteX, currentContactY);
      //   currentContactY += 12; // Move to next line
      // }
      // --- Contact info line 2 (email and phones) ---
      const contactParts = [
        data?.pageHeader?.website,
        data?.pageHeader?.email ? `E-Mail: ${data.pageHeader.email}` : null,
        data?.pageHeader?.phone,
        data?.pageHeader?.phone2
      ].filter(Boolean);
      
      if (contactParts.length > 0) {
        doc.setFontSize(8);
        const contactText = contactParts.join(', ');
        const contactWidth = doc.getTextWidth(contactText);
        const contactX = ((pageWidth - contactWidth) / 2) - 25;
        doc.text(contactText, contactX, currentContactY);
      }

      // --- Header separator line ---
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1);
      doc.line(20, currentContactY + 10, 575, currentContactY + 10);
      
      // --- Patient Information Section (2 columns layout) ---
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      
      // Define label widths for alignment
      const col1LabelWidth = 90;
      const col2LabelWidth = 100;
      
      const patientDataStartY = currentContactY + 20;
      
      // Column 1 (Left)
      drawLabelValue(
        doc,
        "MR No: ",
        data?.patient?.mrn || "--",
        MARGIN_LEFT,
        patientDataStartY,
        col1LabelWidth
      );
      drawLabelValue(
        doc,
        "Ref MR No: ",
        data?.patient?.ref_mrn || "--",
        MARGIN_LEFT,
        patientDataStartY + 13,
        col1LabelWidth
      );
      drawLabelValue(
        doc,
        "Patient Name: ",
        data.patient.patientName || "--",
        MARGIN_LEFT,
        patientDataStartY + 26,
        col1LabelWidth
      );
      drawLabelValue(
        doc,
        "Guardian: ",
        data.patient?.guardiansName || "--",
        MARGIN_LEFT,
        patientDataStartY + 39,
        col1LabelWidth
      );
      drawLabelValue(
        doc,
        "Age/Sex: ",
        `${calculateAge(data.patient.dob, data?.createdAt)} / ${data.patient.gender}`,
        MARGIN_LEFT,
        patientDataStartY + 52,
        col1LabelWidth
      );
      drawLabelValue(
        doc,
        "Phone: ",
        data.patient.phonNumber || "--",
        MARGIN_LEFT,
        patientDataStartY + 65,
        col1LabelWidth
      );

      // Column 2 (Right)
      drawLabelValue(
        doc,
        "TR No: ",
        data?.trNo || "--",
        300,
        patientDataStartY,
        col2LabelWidth
      );
      drawLabelValue(
        doc,
        "Collection Date: ",
        data?.collectionDate ? formatDateTimeWithMonth(data.collectionDate) : "--",
        300,
        patientDataStartY + 13,
        col2LabelWidth
      );
      drawLabelValue(
        doc,
        "Reporting Date: ",
        (() => {
          const reportingDate = getMaxReportingDate(
            data?.testResults,
            data?.reportingDate
          );
          return reportingDate ? formatDateTimeWithMonth(reportingDate) : "--";
        })(),
        300,
        patientDataStartY + 26,
        col2LabelWidth
      );
      drawLabelValue(
        doc,
        "Registered At: ",
        data?.collectionCenter?.collectionCenterName || "--",
        300,
        patientDataStartY + 39,
        col2LabelWidth
      );
      drawLabelValue(
        doc,
        "Referred By: ",
        data?.otherDetails?.referredBy || "--",
        300,
        patientDataStartY + 52,
        col2LabelWidth
      );
      drawLabelValue(
        doc,
        "Collection Type: ",
        data?.otherDetails?.specimenType || "--",
        300,
        patientDataStartY + 65,
        col2LabelWidth
      );
      
      // --- Bottom separator line ---
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(20, patientDataStartY + 83, pageWidth - 20, patientDataStartY + 83);
    };

    /**
     * Estimates the total height of a test including results, comments, notes and methodology.
     * This helps in deciding if the entire test block should move to the next page.
     */
    const getTestTotalHeight = (testResult: TestResult): number => {
      let totalHeight = 0;
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - MARGIN_LEFT - MARGIN_RIGHT;

      // 1. Estimation for the specific renderer
      if (testResult.testResultType === "PCR's special tests") {
        totalHeight += 120; // Header(30) + Boxes(40+30) + extra spacing 20
        if (testResult.method) {
          const methodText = (testResult.method || "").replace(/<[^>]+>/g, "");
          const splitMethod = doc.splitTextToSize(methodText, 500);
          totalHeight += 15 + (splitMethod.length * 12) + 20;
        }
        if (testResult.comments) {
          const commentsText = (testResult.comments || "").replace(/<[^>]+>/g, "");
          const splitComments = doc.splitTextToSize(commentsText, 500);
          totalHeight += 15 + (splitComments.length * 12) + 20;
        }
      } else if (testResult.testResultType === "Lipid profile test") {
        totalHeight += 25; // Main display name header
        let parametersToProcess = testResult.testParameterToPerform.filter(param => param.printOnReport);
        if (parametersToProcess.length === 0) parametersToProcess = testResult.testParameterToPerform;
        
        for (const param of parametersToProcess) {
          const description = param?.interpretation || "there is no interpretation for this parameter.";
          const rightSectionWidth = 200;
          const leftSectionMaxWidth = contentWidth - rightSectionWidth - 20;
          const splitDescription = doc.splitTextToSize(description, leftSectionMaxWidth);
          const interpretationHeight = 15 + (splitDescription.length * 12);
          const resultSectionHeight = 60;
          totalHeight += Math.max(interpretationHeight, resultSectionHeight) + 35; // row height + spacing
        }
      } else if (testResult.testResultType === "Culture Urine" || testResult.testResultType === "Culture") {
        totalHeight += 75; // Header(20) + Specimen(15) + Growth(20) + padding(20)
        let cultureMedicines = testResult.cultureMedicines || [];
        cultureMedicines = cultureMedicines.filter(med => (med.sensitive_1 || "").trim() || (med.sensitive_2 || "").trim() || (med.sensitive_3 || "").trim());
        if (cultureMedicines.length > 0) {
          const mid = Math.ceil((cultureMedicines.length + 5) / 2);
          totalHeight += (mid + 2) * 20 + 70;
        }
      } else {
        totalHeight += 25;
        let parametersToShow = testResult.testParameterToPerform.filter(param => param.printOnReport);
        if (parametersToShow.length > 0) {
           // Precise calculation: 2 header rows + data rows (~17pt per row)
          totalHeight += (parametersToShow.length + 2) * 17 + 35;
        }
      }

      // 2. Comments and Notes
      if (testResult.testResultType !== "PCR's special tests") {
        if (testResult.comments) {
          const splitComments = doc.splitTextToSize(testResult.comments || "", 450);
          totalHeight += 15 + (splitComments.length * 12) + 10;
        }
        if (testResult.notes) {
          const splitNotes = doc.splitTextToSize(testResult.notes || "", 450);
          totalHeight += 12 + (splitNotes.length * 10) + 15;
        }
        if (testResult.method) {
          const splitMethod = doc.splitTextToSize(testResult.method || "", 450);
          totalHeight += 12 + (splitMethod.length * 10) + 15;
        }
      }

      return totalHeight;
    };

    const renderPCRSpecialTest = async (
      testResult: TestResult,
      currentY: number
    ) => {
      // Main Test Header (centered)
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const testTitle =
        testResult.displayName || testResult.testName || "--";
      const titleWidth = doc.getTextWidth(testTitle);
      const pageWidth = doc.internal.pageSize.getWidth();
      const titleX = MARGIN_LEFT;
      doc.text(testTitle, titleX, currentY);

      currentY += 30;

      // Get the main parameters for the two boxes
      let parametersToProcess = testResult.testParameterToPerform.filter((param) => param.printOnReport)
      if (parametersToProcess.length === 0) {
        parametersToProcess = testResult.testParameterToPerform
      }

      // Two boxes layout - centered on page
      const boxWidth = 200;
      const boxHeight = 40;
      const boxY = currentY;
      const boxSpacing = 30; // Space between the two boxes
      const totalWidth = (boxWidth * 2) + boxSpacing; // Total width of both boxes plus spacing
      const leftBoxX = (pageWidth - totalWidth) / 2; // Center the boxes
      const rightBoxX = leftBoxX + boxWidth + boxSpacing;

      // Left Box - Result
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(2);
      doc.rect(leftBoxX, boxY, boxWidth, boxHeight);

      const resultval = testResult?.resultValue || "--";
      // Left Box Content
      const resultText = `Result: ${resultval}`;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const resultTextWidth = doc.getTextWidth(resultText);
      doc.text(
        resultText,
        leftBoxX + (boxWidth - resultTextWidth) / 2,
        boxY + 25
      );

      // Right Box - Viral Load or other measurement
      doc.rect(rightBoxX, boxY, boxWidth, boxHeight);

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const viralLoadTextWidth = doc.getTextWidth(
        `Viral Load: ${testResult?.viralLoad || "--"}`
      );
      doc.text(
        `Viral Load: ${testResult?.viralLoad || "--"}`,
        rightBoxX + (boxWidth - viralLoadTextWidth) / 2,
        boxY + 25
      );

      currentY += boxHeight + 30;
      const htmlDescription = testResult.method || "No description available.";
      const methodText = htmlDescription.replace(/<[^>]+>/g, ""); // Strip HTML tags
      // Methodology Section
      if (testResult.method) {
        const splitMethod = doc.splitTextToSize(methodText, 500);
        const totalMethodHeight = 15 + (splitMethod.length * 12) + 20;
        
        // Check if complete methodology section will fit on current page
        if (currentY + totalMethodHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Methodology:", MARGIN_LEFT, currentY);
        currentY += 15;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(splitMethod, MARGIN_LEFT, currentY);
        currentY += splitMethod.length * 12 + 20;
      }

      // Comments Section
      const htmlComments = testResult.comments || "No comments available.";
      const commentsText = htmlComments.replace(/<[^>]+>/g, ""); // Strip HTML tags
      if (testResult.comments) {
        const splitComments = doc.splitTextToSize(commentsText, 500);
        const totalCommentsHeight = 15 + (splitComments.length * 12) + 20;
        
        // Check if complete comments section will fit on current page
        if (currentY + totalCommentsHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Comments:", MARGIN_LEFT, currentY);
        currentY += 15;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(splitComments, MARGIN_LEFT, currentY);
        currentY += splitComments.length * 12 + 20;
      }

      return currentY + 20;
    };
    const renderCultureUrineTest = async (
      testResult: TestResult,
      currentY: number
    ) => {
      // Test name
      doc.setFont("helvetica", "bold").setFontSize(14);
      doc.text(
        testResult.displayName || testResult.testName || "--",
        MARGIN_LEFT,
        currentY
      );
      currentY += 20;
      // Specimen
      doc.setFont("helvetica", "normal").setFontSize(10);
      doc.text(
        `Specimen: ${
          testResult?.chemicalExamination?.specimen || "No specimen"
        }`,
        MARGIN_LEFT,
        currentY
      );
      currentY += 15;

      // Culture Growth
      doc.text(
        `Culture Growth: ${
          testResult?.chemicalExamination?.cultureGrowth || "--"
        }`,
        MARGIN_LEFT,
        currentY
      );
      currentY += 20;

      // Separator line
      // doc.line(MARGIN_LEFT, currentY, 550, currentY)
      // currentY += 15

      // Build antibiotics list with grouping
      let cultureMedicines = testResult.cultureMedicines || [];
      // Filter medicines that have at least one non-empty sensitive value
      cultureMedicines = cultureMedicines.filter(
        med =>
          (med.sensitive_1 || "").trim() ||
          (med.sensitive_2 || "").trim() ||
          (med.sensitive_3 || "").trim()
      );

      let finalY = currentY;

      // Only show table if there are culture medicines
      if (cultureMedicines.length > 0) {
        // Group by medicineGroup
        const groupMap = new Map<string, CultureMedicine[]>();
        for (const med of cultureMedicines) {
          const grp = med.medicineGroup || "";
          if (!groupMap.has(grp)) {
            groupMap.set(grp, []);
          }
          groupMap.get(grp)!.push(med);
        }
        // Get groups sorted by min sortOrder
        const sortedGroups = Array.from(groupMap.keys()).sort((a, b) => {
          const minA = Math.min(...groupMap.get(a)!.map(m => m.sortOrder));
          const minB = Math.min(...groupMap.get(b)!.map(m => m.sortOrder));
          return minA - minB;
        });
        // Now build flat list
        interface Antibiotic {
          group: string;
          name: string;
          s1: string;
          s2: string;
          s3: string;
        }
        const builtAntibiotics: Antibiotic[] = [];
        for (const groupName of sortedGroups) {
          const meds = groupMap
            .get(groupName)!
            .sort((a, b) => a.sortOrder - b.sortOrder);
          if (groupName) {
            // push header
            builtAntibiotics.push({
              group: groupName,
              name: "",
              s1: "",
              s2: "",
              s3: "",
            });
          }
          // push meds
          for (const med of meds) {
            builtAntibiotics.push({
              group: "",
              name: med.medicineName,
              s1: (med.sensitive_1 || "").toUpperCase(),
              s2: (med.sensitive_2 || "").toUpperCase(),
              s3: (med.sensitive_3 || "").toUpperCase(),
            });
          }
        }

        // Split into left and right
        const mid = Math.ceil(builtAntibiotics.length / 2);
        const leftAntibiotics = builtAntibiotics.slice(0, mid);
        const rightAntibiotics = builtAntibiotics.slice(mid);
        const maxRows = Math.max(leftAntibiotics.length, rightAntibiotics.length);
        const tableBody: any[] = [];

        for (let i = 0; i < maxRows; i++) {
          const left = leftAntibiotics[i] || {
            group: "",
            name: "",
            s1: "",
            s2: "",
            s3: "",
          };
          const right = rightAntibiotics[i] || {
            group: "",
            name: "",
            s1: "",
            s2: "",
            s3: "",
          };

          const leftDisplay = left.group ? left.group : left.name;
          const rightDisplay = right.group ? right.group : right.name;

          tableBody.push([
            leftDisplay,
            left.s1,
            left.s2,
            left.s3,
            rightDisplay,
            right.s1,
            right.s2,
            right.s3,
          ]);
        }

        // Check if table will fit on current page, if not start on new page
        const estimatedTableHeight = (tableBody.length + 1) * 20 + 50; // Approximate height
        if (currentY + estimatedTableHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }

        autoTable(doc, {
          head: [
            ["Antibiotics", "S1", "S2", "S3", "Antibiotics", "S1", "S2", "S3"],
          ],
          body: tableBody,
          startY: currentY - 5,
          theme: "plain",
          styles: {
            fontSize: 12,
            cellPadding: 3,
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            halign: "center",
            fontStyle: "bold",
          },
          columnStyles: {
            0: { halign: "left" },
            1: { halign: "center" },
            2: { halign: "center" },
            3: { halign: "center" },
            4: { halign: "left" },
            5: { halign: "center" },
            6: { halign: "center" },
            7: { halign: "center" },
          },
          margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT, bottom: 90, top: 200 },
          didDrawPage: (hookData) => {
            if (hookData.pageNumber > 1) {
              // Header will be drawn by the footer loop
            }
          },
          didParseCell: data => {
            if (data.section === "body") {
              const row = data.row.raw as string[];
              const isLeftGroup = row[1] === "" && row[2] === "" && row[3] === "";
              const isRightGroup =
                row[5] === "" && row[6] === "" && row[7] === "";
              if (
                (data.column.index === 0 && isLeftGroup) ||
                (data.column.index === 4 && isRightGroup)
              ) {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.fontSize = 9;
              }
            }
          },
        });

        finalY = (doc as any).lastAutoTable.finalY;

        // Legend
        doc.setFontSize(9).setFont("helvetica", "normal");
        doc.text(
          "S= Sensitive               I= Intermediate                R= Resistant",
          170,
          finalY + 20
        );

        currentY = finalY + 50;
      } else {
        // If no culture medicines, skip table and just add spacing
        currentY += 10;
      }

      return currentY;
    };
    const renderParameterizedTest = async (
      testResult: TestResult,
      currentY: number
    ) => {
      // Process parameters that should be shown
      let parametersToShow = testResult.testParameterToPerform.filter(
        param => param.printOnReport
      );
      // If no parameters are marked for printing, return early (don't show any parameters)
      if (parametersToShow.length === 0) {
        return currentY;
      }
      
      // Render test section header
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(
        testResult.displayName || testResult.testName || "Unknown Test",
        MARGIN_LEFT,
        currentY
      );
      currentY += 20;
      
      // Check if this is a parameterized test
      const isParameterized = testResult.testResultType === "Parameterized";
      // Check if this is a parameterized test with patient values
      const hasPatientValues =
        testResult.testResultType === "Parameterized" &&
        testResult.testResultTemplate === "Test Result with Patient Values";
      // Check if this is a device method report (only Test and Result columns)
      const isDeviceMethodReport =
        testResult.testResultTemplate === "Device Method Report";

      // Check if history data exists and should be shown (NOT for Patient Values template)
      const hasHistory =
        testResult.history &&
        testResult.history.length > 0 &&
        !hasPatientValues;
      const numHistory = hasHistory
        ? Math.min(testResult.history.length, 3)
        : 0;
      // Prepare history dates if history exists (prefer approvedOn, then performedOn)
      const historyDates: any[] = [];
      if (hasHistory) {
        for (let i = 0; i < numHistory; i++) {
          const historyEntry = testResult.history[i];
          if (!historyEntry) continue;
          const historyDate =
            historyEntry.approvedOn ||
            historyEntry.performedOn ||
            historyEntry.createdAt ||
            "";
          historyDates.push(formatDateTimeWithMonth(historyDate));
        }
      }

      // Prepare table data with arrow info stored separately
      const resultArrowMap = new Map<number, { isHigh: boolean; isLow: boolean }>();
      const historyArrowMap = new Map<string, { isHigh: boolean; isLow: boolean }>();
      
      const tableData = parametersToShow.map((param, index) => {
        // Use normalValues
        let normalRange = param.normalValues;
        // Use patientType unit if available, otherwise use param units
        let units = param.units || "";
        if (param.patientType && param.patientType.unit_cutOff) {
          units = param.patientType.unit_cutOff;
        }
        
        // Check if result is high or low
        const { isHigh, isLow } = isWithinNormalRange(
          param.result,
          param.normalValues,
          param.patientType
        );
        
        // Store arrow info for custom drawing
        resultArrowMap.set(index, { isHigh, isLow });
        
        // Use result without arrow indicator in table data
        let resultValue = param.result || "--";
        
        // Prepare test column content with parameterType above parameter name
        // AutoTable accepts arrays for multiline cells
        const testColumnContent = param.patientType?.parameterType 
          ? [param.patientType.parameterType, param.parameter]
          : param.parameter;
        
        // Prepare history results if history exists
        const historyResults: string[] = [];
        if (hasHistory) {
          // Show history results for parameterized tests (excluding Patient Values template)
          for (let i = 0; i < numHistory; i++) {
            const historyEntry = testResult.history[i];
            const matchingHistoryParam =
              historyEntry.testParameterToPerform.find(
                histParam => histParam.parameter === param.parameter
              );

            const histResult = matchingHistoryParam?.result || "-";
            historyResults.push(histResult);
            
            // Store arrow info for history results
            if (histResult && histResult !== "-") {
              const historyCheck = isWithinNormalRange(
                histResult,
                param.normalValues,
                param.patientType
              );
              historyArrowMap.set(`${index}-${i}`, { 
                isHigh: historyCheck.isHigh, 
                isLow: historyCheck.isLow 
              });
            }
          }
        }

        // Build row data based on test template type
        let rowData;
        if (isDeviceMethodReport) {
          // Device Method Report: Test, Normal Range, Result, History
          if (hasHistory) {
            rowData = [
              testColumnContent,
              normalRange, // Normal Range
              resultValue,
              ...historyResults,
            ];
          } else {
            // No history: Test, Normal Range, Result
            rowData = [testColumnContent, normalRange, resultValue];
          }
        } else {
          // Standard layout: Normal Range or Patient Value in same position after Test
          rowData = [
            testColumnContent,
            ...(isParameterized && !hasPatientValues ? [normalRange] : []),
            ...(hasPatientValues ? [param.patientValue || "--"] : []),
            units,
            resultValue,
            ...historyResults,
          ];
        }

        return rowData;
      });
      // Define table headers based on test template type
      let baseHeaders;
      if (isDeviceMethodReport) {
        // Device Method Report: Test, Normal Range, Result, History
        if (hasHistory) {
          baseHeaders = [
            "Test",
            "Normal Range",
            "Result",
            ...Array(numHistory).fill("History"),
          ];
        } else {
          baseHeaders = ["Test", "Normal Range", "Result"];
        }
      } else {
        // Standard layout based on parameterized test and patient values
        baseHeaders = ["Test"];
        if (isParameterized && !hasPatientValues) {
          baseHeaders.push("Normal Range");
        }
        if (hasPatientValues) {
          baseHeaders.push("Patient Value");
        }
        baseHeaders.push("Unit");
        baseHeaders.push("Result");
      }

      const tableHead = hasHistory
        ? [
            isDeviceMethodReport
              ? baseHeaders
              : [...baseHeaders, ...Array(numHistory).fill("History")],
            isDeviceMethodReport
              ? [
                  "", // Test column
                  "", // Normal Range - empty
                  "", // Result column
                  ...historyDates, // History dates at the end
                ]
              : [
                  "", // Test column
                  ...(isParameterized && !hasPatientValues ? [""] : []), // Normal Range
                  ...(hasPatientValues ? [""] : []), // Patient Value
                  "", // Unit column
                  "", // Result column
                  ...historyDates,
                ],
          ]
        : [baseHeaders];
      // Calculate total table width and columns
      const pageWidth = doc.internal.pageSize.getWidth();
      const totalTableWidth = (pageWidth - MARGIN_LEFT - MARGIN_RIGHT) * 0.85; // Reduce to 85% of full width

      // Calculate base columns based on template type
      let baseCols;
      if (isDeviceMethodReport) {
        // Device Method Report: Test, Normal Range, Result, History (3 base + history)
        baseCols = 3 + (hasHistory ? numHistory : 0);
      } else {
        baseCols = 3; // Test, Unit, Result (minimum)
        if (isParameterized && !hasPatientValues) baseCols++; // Add Normal Range (only if no patient values)
        if (hasPatientValues) baseCols++; // Add Patient Value
      }

      const totalCols = isDeviceMethodReport
        ? baseCols // For Device Method Report, baseCols already includes everything
        : baseCols + (hasHistory ? numHistory : 0);
      
      // Calculate column widths - fixed for Test, Range, Unit always
      let columnWidths: number[] = [];
      
      // Fixed widths for consistent appearance.
      // Test gives up 34pt so Normal Range and Unit gain 16pt and 18pt — the combined
      // width stays at 280pt, keeping the table aligned with the rest of the layout.
      const fixedTestWidth = 130;
      const fixedRangeWidth = 92;
      const fixedUnitWidth = 58;
      const fixedResultWidth = 71;
      
      if (isDeviceMethodReport) {
        if (hasHistory) {
          const remainingWidth = totalTableWidth - fixedTestWidth - fixedRangeWidth - fixedResultWidth;
          const historyWidth = 71;
          
          columnWidths = [fixedTestWidth, fixedRangeWidth, fixedResultWidth];
          for (let i = 0; i < numHistory; i++) columnWidths.push(historyWidth);
        } else {
          columnWidths = [fixedTestWidth, fixedRangeWidth, fixedResultWidth];
        }
      } else {
        const baseFixedWidth = fixedTestWidth + fixedUnitWidth + fixedResultWidth;
        const totalFixedWidth = baseFixedWidth + (isParameterized && !hasPatientValues ? fixedRangeWidth : 0) + (hasPatientValues ? fixedRangeWidth : 0);
        
        if (hasHistory) {
          const remainingWidth = totalTableWidth - totalFixedWidth;
          const historyWidth = 71;
          
          columnWidths = [fixedTestWidth];
          if (isParameterized && !hasPatientValues) columnWidths.push(fixedRangeWidth);
          if (hasPatientValues) columnWidths.push(fixedRangeWidth);
          columnWidths.push(fixedUnitWidth, fixedResultWidth);
          for (let i = 0; i < numHistory; i++) columnWidths.push(historyWidth);
        } else {
          columnWidths = [fixedTestWidth];
          if (isParameterized && !hasPatientValues) columnWidths.push(fixedRangeWidth);
          if (hasPatientValues) columnWidths.push(fixedRangeWidth);
          columnWidths.push(fixedUnitWidth, fixedResultWidth);
        }
      }

      const columnStyles: { [key: string]: Partial<Styles> } = {};

      for (let col = 0; col < totalCols; col++) {
        columnStyles[col.toString()] = {
          cellWidth: columnWidths[col],
          halign: col === 0 ? "left" as const : "center" as const,
        };
      }

      // Make the Result column bold (calculate correct index based on template)
      let resultColIndex;
      if (isDeviceMethodReport) {
        // For Device Method Report, Result column is always at index 2 (3rd column)
        resultColIndex = 2;
      } else {
        resultColIndex = 1; // Start after Test column
        if (isParameterized && !hasPatientValues) resultColIndex++; // Skip Normal Range (only if no patient values)
        resultColIndex++; // Skip Unit column
        if (hasPatientValues) resultColIndex++; // Skip Patient Value
        // resultColIndex now points to Result column
      }

      columnStyles[resultColIndex.toString()] = {
        ...columnStyles[resultColIndex.toString()],
        fontStyle: "bold",
      };
      // Result column date: prefer approvedOn, fall back to performedOn
      const resultDate = testResult.approvedOn || testResult.performedOn;
      if (resultDate && hasHistory) {
        tableHead[1][resultColIndex] = formatDateTimeWithMonth(resultDate);
      } else if (resultDate && !hasHistory) {
        const totalCols = tableHead[0].length;
        const createdAtRow = Array(totalCols).fill("");
        createdAtRow[resultColIndex] = formatDateTimeWithMonth(resultDate);
        tableHead.push(createdAtRow);
      }
      
      // Store resultColIndex for use in didParseCell
      const resultColumnIndex = resultColIndex;
      
      // Render the table
      autoTable(doc, {
        startY: currentY - 5,
        head: tableHead,
        body: tableData,
        theme: "plain",
        tableWidth: totalTableWidth,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          halign: "center",
          valign: "middle",
          lineColor: [180, 180, 180],
          lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
          fillColor: [255, 255, 255],
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 9,
          lineColor: [0, 0, 0],
          lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
          halign: "center",
          valign: "middle",
        },
        bodyStyles: {
          lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
          fillColor: [255, 255, 255],
          lineColor: [180, 180, 180],
        },
        columnStyles: columnStyles,
        margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT, bottom: 120, top: 200 },
        didDrawPage: (hookData) => {
          if (hookData.pageNumber > 1) {
            // Header will be drawn by the footer loop
          }
        },
        willDrawCell: (data) => {
          // Draw full-width gray background for header before cell content
          if (data.section === 'head' && data.column.index === 0) {
            const cellY = data.cell.y;
            const cellHeight = data.cell.height;
            doc.setFillColor(240, 240, 240);
            doc.rect(MARGIN_LEFT, cellY, pageWidth - MARGIN_LEFT - MARGIN_RIGHT, cellHeight, 'F');
          }
        },
        didDrawCell: (data) => {
          // Draw full-width horizontal lines after cell content
          if (data.section === 'head') {
            const isFirstColumn = data.column.index === 0;
            const isLastColumn = data.column.index === data.table.columns.length - 1;
            
            if (isFirstColumn || isLastColumn) {
              const cellY = data.cell.y;
              const cellHeight = data.cell.height;
              
              doc.setDrawColor(0, 0, 0);
              doc.setLineWidth(0.5);
              doc.line(MARGIN_LEFT, cellY, pageWidth - MARGIN_RIGHT, cellY);
              doc.line(MARGIN_LEFT, cellY + cellHeight, pageWidth - MARGIN_RIGHT, cellY + cellHeight);
            }
          }
          
          // Draw full-width horizontal lines for body
          if (data.section === 'body') {
            const isFirstColumn = data.column.index === 0;
            const isLastColumn = data.column.index === data.table.columns.length - 1;
            
            if (isFirstColumn || isLastColumn) {
              const cellY = data.cell.y;
              const cellHeight = data.cell.height;
              
              doc.setDrawColor(180, 180, 180);
              doc.setLineWidth(0.1);
              doc.line(MARGIN_LEFT, cellY, pageWidth - MARGIN_RIGHT, cellY);
              doc.line(MARGIN_LEFT, cellY + cellHeight, pageWidth - MARGIN_RIGHT, cellY + cellHeight);
            }
            
            // Custom draw for Test column with parameterType
            if (data.column.index === 0 && Array.isArray(data.cell.raw) && data.cell.raw.length === 2) {
              const padding = 4; // Equal padding from top, between items, and bottom
              const paramTypeFontSize = 8;
              const paramNameFontSize = 9;
              
              // Calculate total content height: paramType + padding + paramName
              const totalContentHeight = paramTypeFontSize + padding + paramNameFontSize;
              
              // Calculate starting Y to center content vertically with equal padding
              const startY = data.cell.y + (data.cell.height - totalContentHeight) / 2;
              
              // Draw parameterType (bold) left-aligned on first line
              doc.setFont("helvetica", "bold");
              doc.setFontSize(paramTypeFontSize);
              doc.setTextColor(0, 0, 0);
              const paramTypeY = startY + paramTypeFontSize; // baseline position
              doc.text(data.cell.raw[0], data.cell.x + 4, paramTypeY);
              
              // Draw parameter name (normal) on second line with equal padding
              doc.setFont("helvetica", "normal");
              doc.setFontSize(paramNameFontSize);
              const paramNameY = paramTypeY + padding + paramNameFontSize; // padding + baseline
              doc.text(data.cell.raw[1], data.cell.x + 4, paramNameY);
            }
            
            // Draw arrow shapes for Result column and History columns
            const rowIndex = data.row.index;
            
            // Check if this is Result column or History column
            if (data.column.index === resultColumnIndex) {
              // Result column arrows
              const arrowInfo = resultArrowMap.get(rowIndex);
              
              if (arrowInfo && (arrowInfo.isHigh || arrowInfo.isLow)) {
                const cellX = data.cell.x;
                const cellY = data.cell.y;
                const cellWidth = data.cell.width;
                const cellHeight = data.cell.height;
                
                // Get text width and position arrow closer to the value
                const cellText = data.cell.text[0] || "";
                const textWidth = doc.getTextWidth(cellText);
                const centerX = cellX + cellWidth / 2;
                const arrowX = centerX + textWidth / 2 + 5;
                const arrowY = cellY + cellHeight / 2;
                
                if (arrowInfo.isHigh) {
                  // Draw up arrow (red)
                  doc.setFillColor(220, 38, 38);
                  doc.setDrawColor(220, 38, 38);
                  doc.setLineWidth(1);
                  // Arrow shaft
                  doc.line(arrowX, arrowY + 3, arrowX, arrowY - 3);
                  // Arrow head (up)
                  doc.line(arrowX, arrowY - 3, arrowX - 2, arrowY - 1);
                  doc.line(arrowX, arrowY - 3, arrowX + 2, arrowY - 1);
                } else if (arrowInfo.isLow) {
                  // Draw down arrow (red)
                  doc.setFillColor(220, 38, 38);
                  doc.setDrawColor(220, 38, 38);
                  doc.setLineWidth(1);
                  // Arrow shaft
                  doc.line(arrowX, arrowY - 3, arrowX, arrowY + 3);
                  // Arrow head (down)
                  doc.line(arrowX, arrowY + 3, arrowX - 2, arrowY + 1);
                  doc.line(arrowX, arrowY + 3, arrowX + 2, arrowY + 1);
                }
              }
            } else if (data.column.index > resultColumnIndex) {
              // History column arrows
              const historyIndex = data.column.index - resultColumnIndex - 1;
              const arrowInfo = historyArrowMap.get(`${rowIndex}-${historyIndex}`);
              
              if (arrowInfo && (arrowInfo.isHigh || arrowInfo.isLow)) {
                const cellX = data.cell.x;
                const cellY = data.cell.y;
                const cellWidth = data.cell.width;
                const cellHeight = data.cell.height;
                
                // Get text width and position arrow closer to the value
                const cellText = data.cell.text[0] || "";
                const textWidth = doc.getTextWidth(cellText);
                const centerX = cellX + cellWidth / 2;
                const arrowX = centerX + textWidth / 2 + 5;
                const arrowY = cellY + cellHeight / 2;
                
                if (arrowInfo.isHigh) {
                  // Draw up arrow (red)
                  doc.setFillColor(220, 38, 38);
                  doc.setDrawColor(220, 38, 38);
                  doc.setLineWidth(1);
                  // Arrow shaft
                  doc.line(arrowX, arrowY + 3, arrowX, arrowY - 3);
                  // Arrow head (up)
                  doc.line(arrowX, arrowY - 3, arrowX - 2, arrowY - 1);
                  doc.line(arrowX, arrowY - 3, arrowX + 2, arrowY - 1);
                } else if (arrowInfo.isLow) {
                  // Draw down arrow (red)
                  doc.setFillColor(220, 38, 38);
                  doc.setDrawColor(220, 38, 38);
                  doc.setLineWidth(1);
                  // Arrow shaft
                  doc.line(arrowX, arrowY - 3, arrowX, arrowY + 3);
                  // Arrow head (down)
                  doc.line(arrowX, arrowY + 3, arrowX - 2, arrowY + 1);
                  doc.line(arrowX, arrowY + 3, arrowX + 2, arrowY + 1);
                }
              }
            }
          }
        },
        didParseCell: data => {
          // Hide default text rendering for Test column with parameterType array
          if (data.section === "body" && data.column.index === 0) {
            if (Array.isArray(data.cell.raw) && data.cell.raw.length === 2) {
              data.cell.text = []; // Clear text so we can draw it manually
              // Increase row height to accommodate parameterType + padding + parameterName
              // padding(4) + paramTypeFontSize(8) + padding(4) + paramNameFontSize(9) + padding(4) = 29
              data.cell.styles.minCellHeight = 25;
            }
          }
          
          if (data.section === "head" && data.row.index > 0) {
            data.cell.styles.fontSize = 6;
          }
          // Set black bottom border for last row in header
          if (data.section === "head") {
            const isLastHeaderRow = data.row.index === data.table.head.length - 1;
            if (isLastHeaderRow) {
              data.cell.styles.lineColor = [0, 0, 0];
              data.cell.styles.lineWidth = { top: 0.1, right: 0, bottom: 0.5, left: 0 };
            }
          }
          // Color the Result column and History columns based on normal range
          if (data.section === "body") {
            const rowIndex = data.row.index;
            
            // Color Result column
            if (data.column.index === resultColumnIndex) {
              const arrowInfo = resultArrowMap.get(rowIndex);
              if (arrowInfo) {
                if (arrowInfo.isHigh || arrowInfo.isLow) {
                  // Red for abnormal (high or low)
                  data.cell.styles.textColor = [220, 38, 38];
                  data.cell.styles.fontStyle = 'bold';
                } else {
                  // Black for normal
                  data.cell.styles.textColor = [0, 0, 0];
                }
              }
            }
            // Color History columns
            else if (data.column.index > resultColumnIndex) {
              const historyIndex = data.column.index - resultColumnIndex - 1;
              const arrowInfo = historyArrowMap.get(`${rowIndex}-${historyIndex}`);
              if (arrowInfo) {
                if (arrowInfo.isHigh || arrowInfo.isLow) {
                  // Red for abnormal (high or low)
                  data.cell.styles.textColor = [220, 38, 38];
                  data.cell.styles.fontStyle = 'bold';
                } else {
                  // Black for normal
                  data.cell.styles.textColor = [0, 0, 0];
                }
              }
            }
          }
          // Remove first and last column lines from header
          if (data.section === "head") {
            // Remove bottom border from first header row
            if (data.row.index === 0) {
              if (data.column.index === 0) {
                data.cell.styles.lineWidth = { top: 0.5, right: 0.5, bottom: 0, left: 0 };
              } else if (data.column.index === data.table.columns.length - 1) {
                data.cell.styles.lineWidth = { top: 0.5, right: 0, bottom: 0, left: 0.5 };
              } else {
                // Check if this is Result column or History columns (need to identify by index)
                // Result column is at resultColumnIndex, history columns follow after
                const isResultOrHistory = data.column.index >= resultColumnIndex;
                if (isResultOrHistory) {
                  data.cell.styles.lineWidth = { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 };
                } else {
                  data.cell.styles.lineWidth = { top: 0.5, right: 0.5, bottom: 0, left: 0.5 };
                }
              }
            }
            // Add bottom border only to date cells in second header row (Result and History columns)
            else if (data.row.index === 1) {
              // Only add bottom border to Result and History columns with dates
              const isResultOrHistory = data.column.index >= resultColumnIndex;
              if (isResultOrHistory && data.cell.text && data.cell.text[0] !== "") {
                // Cell has date content in Result/History columns - add black bottom border
                data.cell.styles.lineColor = [0, 0, 0];
                if (data.column.index === 0) {
                  data.cell.styles.lineWidth = { top: 0.5, right: 0.5, bottom: 0.5, left: 0 };
                } else if (data.column.index === data.table.columns.length - 1) {
                  data.cell.styles.lineWidth = { top: 0.5, right: 0, bottom: 0.5, left: 0.5 };
                } else {
                  data.cell.styles.lineWidth = { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 };
                }
              } else {
                // Empty cell or non-Result/History columns - no borders
                if (data.column.index === 0) {
                  data.cell.styles.lineWidth = { top: 0, right: 0.5, bottom: 0, left: 0 };
                } else if (data.column.index === data.table.columns.length - 1) {
                  data.cell.styles.lineWidth = { top: 0, right: 0, bottom: 0, left: 0.5 };
                } else {
                  data.cell.styles.lineWidth = { top: 0, right: 0.5, bottom: 0, left: 0.5 };
                }
              }
            }
          }
        },
      });
      return (doc as any).lastAutoTable.finalY + 20;
    };
    const renderGroupedLipidProfile = async (
      testResult: TestResult,
      currentY: number
    ) => {
      // Calculate page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - MARGIN_LEFT - MARGIN_RIGHT;
      const rightEdge = pageWidth - MARGIN_RIGHT;
      
      // Define the width reserved for the right-side result section
      const rightSectionWidth = 200; // Width for result, range, and boxes
      const leftSectionMaxWidth = contentWidth - rightSectionWidth - 20; // 20px gap between sections
      
      // Main Test Display Name Header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(
        testResult.displayName || testResult.testName || "Unknown Test",
        MARGIN_LEFT,
        currentY
      );
      // Add test method and general info if available
      // if (testResult.method) {
      //   doc.setFontSize(10);
      //   doc.setFont("helvetica", "normal");
      //   doc.setTextColor(0, 0, 0);
      //   doc.text(`Method: ${testResult.method}`, MARGIN_LEFT, currentY + 20);
      // }
      currentY += 25;
      // Get parameters to display
      let parametersToProcess = testResult.testParameterToPerform.filter(
        param => param.printOnReport
      );
      if (parametersToProcess.length === 0) {
        parametersToProcess = testResult.testParameterToPerform;
      }
      // Render each parameter under the main display name
      for (const param of parametersToProcess) {
        const description =
          param?.interpretation ||
          "there is no interpretation for this parameter.";
        
        // Set font properties BEFORE calculating text wrapping to ensure consistent width
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        
        // Calculate required space for this parameter - use leftSectionMaxWidth to prevent overlap
        const splitDescription = doc.splitTextToSize(description, leftSectionMaxWidth);
        const parameterNameLines = Math.ceil(
          doc.getTextWidth(param.parameter || "Unknown Parameter") / 300
        );
        // Base height starts smaller (50px) but increases with content
        const baseHeight = 50;
        // const descriptionHeight = splitDescription.length * 12;
        const additionalSpacing = 15;
        const requiredHeight = baseHeight + additionalSpacing;
        // Check for page break
        if (currentY + 30 > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }

        // Calculate the total height of interpretation section
        const interpretationHeight = 15 + (splitDescription.length * 12);
        const resultSectionHeight = 60; // Result + range + boxes
        
        // Use the natural max height without forcing minimum
        const calculatedMaxHeight = Math.max(interpretationHeight, resultSectionHeight);
        
        // Check if complete parameter will fit on current page
        if (currentY + calculatedMaxHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        // Store starting Y position for vertical centering
        const rowStartY = currentY;
        const maxRowHeight = calculatedMaxHeight;
        
        // Left side - Parameter Name and Description
        let leftY = rowStartY;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(
          param.parameter || "Unknown Parameter",
          MARGIN_LEFT + 20,
          leftY
        );
        leftY += 15;
        
        // Parameter Description
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60, 60, 60);
        for (let i = 0; i < splitDescription.length; i++) {
          doc.text(splitDescription[i], MARGIN_LEFT + 20, leftY);
          leftY += 12;
        }
        
        // Right side - Result Section (vertically centered with interpretation)
        const resultValue = param.result || "--";
        const { isHigh, isLow, status } = isWithinNormalRange(
          param.result,
          param.normalValues,
          param.patientType
        );
        const resultColor = getResultColor(param.patientType, isHigh, isLow);
        
        // Calculate vertical center position for result section
        const resultVerticalOffset = (maxRowHeight - resultSectionHeight) / 2;
        const resultStartY = rowStartY + resultVerticalOffset;
        
        // Calculate range width
        const rangeWidth = 80;
        const rangeStartX = rightEdge - rangeWidth - 45;
        const rangeCenterX = rangeStartX + (rangeWidth / 2);
        
        // Main Result Display - centered above the min-max range
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...resultColor);
        const resultText = `${resultValue} ${param.units || ""}`;
        const resultTextWidth = doc.getTextWidth(resultText);
        doc.text(resultText, rangeCenterX, resultStartY, {
          align: "center",
        });
        
        // Draw arrow next to result
        if (isHigh || isLow) {
          const arrowX = rangeCenterX + (resultTextWidth / 2) + 5;
          const arrowY = resultStartY - 3;
          
          // Use the same color as the result value
          doc.setDrawColor(...resultColor);
          doc.setLineWidth(2);
          
          if (isHigh) {
            // Draw up arrow - larger size
            doc.line(arrowX, arrowY + 7, arrowX, arrowY - 7);
            doc.line(arrowX, arrowY - 7, arrowX - 4, arrowY - 3);
            doc.line(arrowX, arrowY - 7, arrowX + 4, arrowY - 3);
          } else if (isLow) {
            // Draw down arrow - larger size
            doc.line(arrowX, arrowY - 7, arrowX, arrowY + 7);
            doc.line(arrowX, arrowY + 7, arrowX - 4, arrowY + 3);
            doc.line(arrowX, arrowY + 7, arrowX + 4, arrowY + 3);
          }
        }
        
        // Normal Range Display
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        let normalRangeText = param.normalValues;
        // Always show normalValues
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(`${normalRangeText}`, rangeCenterX, resultStartY + 16, {
          align: "center",
        });
        
        // Status Indicator - professional colored boxes with text and values
        const boxWidth = 55; // Increased width for better appearance
        const boxHeight = 15; // Back to single line height
        const boxSpacing = 5; // Reduced spacing
        const totalBoxesWidth = (boxWidth * 3) + (boxSpacing * 2);
        const boxesStartX = rangeCenterX - (totalBoxesWidth / 2);
        const boxesY = resultStartY + 28;

        const minVal = param.patientType?.min ?? 0;
        const maxVal = param.patientType?.max ?? 0;

        // Low box
        const lowColor = parseHexColor(param.patientType?.belowMinValueColor || "#FF6B6B");
        doc.setFillColor(...lowColor);
        doc.rect(boxesStartX, boxesY, boxWidth, boxHeight, 'F'); // Fill only, no border
        doc.setTextColor(255, 255, 255); // White text
        doc.setFontSize(8); // Smaller font for combined text
        doc.setFont("helvetica", "bold");
        const lowLabel = param.patientType?.belowMinValueText || "Low";
        const lowText = `${lowLabel} (< ${minVal})`;
        const lowTextWidth = doc.getTextWidth(lowText);
        doc.text(lowText, boxesStartX + (boxWidth - lowTextWidth) / 2, boxesY + 10);

        // Normal box
        const normalColor = parseHexColor(param.patientType?.noramalRangeColor || "#4CAF50");
        doc.setFillColor(...normalColor);
        const normalBoxX = boxesStartX + boxWidth + boxSpacing;
        doc.rect(normalBoxX, boxesY, boxWidth, boxHeight, 'F'); // Fill only, no border
        doc.setTextColor(0, 0, 0); // Black text
        const normalLabel = param.patientType?.normalRangeText || "Normal";
        const normalText = `${normalLabel}`;
        const normalTextWidth = doc.getTextWidth(normalText);
        doc.text(normalText, normalBoxX + (boxWidth - normalTextWidth) / 2, boxesY + 10);

        // High box
        const highColor = parseHexColor(param.patientType?.aboveMaxValueColor || "#FF9800");
        doc.setFillColor(...highColor);
        const highBoxX = normalBoxX + boxWidth + boxSpacing;
        doc.rect(highBoxX, boxesY, boxWidth, boxHeight, 'F'); // Fill only, no border
        doc.setTextColor(255, 255, 255); // White text
        const highLabel = param.patientType?.aboveMaxValueText || "High";
        const highText = `${highLabel} (> ${maxVal})`;
        const highTextWidth = doc.getTextWidth(highText);
        doc.text(highText, highBoxX + (boxWidth - highTextWidth) / 2, boxesY + 10);
        
        // Draw separator line after the row with consistent spacing
        const separatorY = rowStartY + maxRowHeight + 15;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(
          MARGIN_LEFT + 10,
          separatorY,
          pageWidth - MARGIN_RIGHT,
          separatorY
        );
        
        // Move to next parameter position with consistent spacing
        currentY = separatorY + 20;
      }
      
      return currentY + 5;
    };
    // Group test results by testResultType while preserving first-appearance order
    const typeOrder = new Map<string, number>();
    let orderCounter = 0;
    
    (data?.testResults || []).forEach(test => {
      const type = String(test.testResultType || "");
      if (!typeOrder.has(type)) {
        typeOrder.set(type, orderCounter++);
      }
    });

    const groupedTestResults = [...(data?.testResults || [])].sort((a, b) => {
      const typeA = String(a.testResultType || "");
      const typeB = String(b.testResultType || "");
      return (typeOrder.get(typeA) ?? 0) - (typeOrder.get(typeB) ?? 0);
    });

    // Initialize PDF
    await addHeaderAndPatientInfo();
    let currentY = 195;
    let previousTestResultType: any = null;

    for (const testResult of groupedTestResults) {
      // If the test result type is different from the previous one, start on a new page
      if (
        previousTestResultType !== null &&
        previousTestResultType !== testResult.testResultType
      ) {
        // If we are already near top (190), no need to add another page
        if (currentY > 195) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
      }
      previousTestResultType = testResult.testResultType;

      // Logic from USER: if any comment/note/method doesn't fit, move COMPLETE test to next page
      const totalTestHeight = getTestTotalHeight(testResult);
      if (currentY + totalTestHeight > PAGE_HEIGHT) {
        doc.addPage();
        await addHeaderAndPatientInfo();
        currentY = 190;
      }

      if (testResult.testResultType === "PCR's special tests") {
        currentY = await renderPCRSpecialTest(testResult, currentY);
      } else if (testResult.testResultType === "Lipid profile test") {
        currentY = await renderGroupedLipidProfile(testResult, currentY);
      } else if (
        testResult.testResultType === "Culture Urine" ||
        testResult.testResultType === "Culture"
      ) {
        currentY = await renderCultureUrineTest(testResult, currentY);
      } else {
        // Don't render test header here - let renderParameterizedTest handle it
        currentY = await renderParameterizedTest(testResult, currentY);
      }
      // Add comments and notes sections
      if (
        testResult.comments &&
        testResult.testResultType !== "PCR's special tests"
      ) {
        const splitComments = doc.splitTextToSize(
          testResult.comments || "",
          450
        );
        const totalCommentsHeight = 15 + (splitComments.length * 12) + 10;
        
        // Check if complete comments section will fit on current page
        if (currentY + totalCommentsHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Comments:", MARGIN_LEFT, currentY);
        currentY += 15;
        
        doc.setFont("helvetica", "normal");
        doc.text(splitComments, MARGIN_LEFT, currentY);
        currentY += splitComments.length * 12 + 10;
      }
      if (testResult.notes) {
        const splitNotes = doc.splitTextToSize(testResult.notes || "", 450);
        const totalNotesHeight = 12 + (splitNotes.length * 10) + 15;
        
        // Check if complete notes section will fit on current page
        if (currentY + totalNotesHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Notes:", MARGIN_LEFT, currentY);
        currentY += 12;
        
        doc.setFont("helvetica", "normal");
        doc.text(splitNotes, MARGIN_LEFT, currentY);
        currentY += splitNotes.length * 10 + 15;
      }
      if (
        testResult.method &&
        testResult.testResultType !== "PCR's special tests"
      ) {
        const splitMethod = doc.splitTextToSize(testResult.method || "", 450);
        const totalMethodHeight = 12 + (splitMethod.length * 10) + 15;
        
        // Check if complete method section will fit on current page
        if (currentY + totalMethodHeight > PAGE_HEIGHT) {
          doc.addPage();
          await addHeaderAndPatientInfo();
          currentY = 190;
        }
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Method:", MARGIN_LEFT, currentY);
        currentY += 12;
        
        doc.setFont("helvetica", "normal");
        doc.text(splitMethod, MARGIN_LEFT, currentY);
        currentY += splitMethod.length * 10 + 15;
      }
    }
    let neqasLogo: string | null = null;
    const verifiedByNeqas = await fetchVerifiedByNeqas(
      projectProfile?.verifiedByNeqas
    );
    if (verifiedByNeqas) {
      neqasLogo = await loadNeqasLogo();
    }

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Draw header on pages after the first one (for table continuations)
      if (i > 1) {
        await addHeaderAndPatientInfo();
      }
      
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      const footerStartY = pageHeight - 80;

      // Electronically verified text
      const savedVerification = data?.pageFooter?.verificationText;
      const verificationText =
        typeof savedVerification === "string"
          ? savedVerification.trim()
          : "Electronically verified. No signature required";
      if (verificationText) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(verificationText, pageWidth / 2, footerStartY, {
          align: "center",
        });
      }
      if (neqasLogo) {
        drawNeqasLogoBesideVerification(
          doc,
          neqasLogo,
          pageWidth,
          footerStartY
        );
      }

      // Top line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(2);
      doc.line(20, footerStartY + 5, pageWidth - 20, footerStartY + 5);

      // Footer roles section
      const footerData = data?.pageFooter;

      if (footerData) {
        const roles = ["Pathologist", "Microbiologist", "Biochemist", "Technologist"] as const;
        const signatories = roles.filter((role) => {
          const person = footerData[role];
          return (
            person?.name?.trim() ||
            person?.qualification?.trim() ||
            person?.designation?.trim()
          );
        });

        if (signatories.length) {
          const columnWidth = (pageWidth - 40) / signatories.length;
          const startX = 20;

          doc.setFontSize(8);
          signatories.forEach((role, idx) => {
            const roleData = footerData[role];
            const xPos = startX + idx * columnWidth;

            if (idx > 0) {
              doc.setDrawColor(0, 0, 0);
              doc.setLineWidth(1);
              doc.line(xPos - 10, footerStartY + 5, xPos - 10, footerStartY + 40);
            }

            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(8);
            doc.text(roleData?.name?.trim() || "", xPos + 5, footerStartY + 15);

            doc.setFont("helvetica", "normal");
            doc.setTextColor(60, 60, 60);
            doc.setFontSize(7);
            doc.text(roleData?.qualification?.trim() || "", xPos + 5, footerStartY + 24);
            doc.text(roleData?.designation?.trim() || "", xPos + 5, footerStartY + 31);
          });
        }
      }

      // Bottom line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(2);
      doc.line(20, footerStartY + 38, pageWidth - 20, footerStartY + 38);

      // Left: Printing time + Printed By; right: Registered By
      doc.setFontSize(7);
      
      // Printing Time label (bold and black)
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const printingTimeLabel = "Printing Time : ";
      doc.text(printingTimeLabel, 25, footerStartY + 50);
      
      // Printing Time value (normal and gray)
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const printingTimeLabelWidth = doc.getTextWidth(printingTimeLabel);
      doc.text( 
        new Date().toLocaleString(),
        25 + printingTimeLabelWidth,
        footerStartY + 50
      );
      
      // Printed By label (bold and black)
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const printedByLabel = "Printed By: ";
      doc.text(printedByLabel, 25, footerStartY + 60);
      
      // Printed By value (normal and gray) — leading space after colon
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const printedByLabelWidth = doc.getTextWidth(printedByLabel);
      const printedByName =
        profile?.data?.fullName ||
        profile?.data?.firstName ||
        data?.pageHeader?.title ||
        "System";
      doc.text(
        ` ${printedByName}`,
        25 + printedByLabelWidth,
        footerStartY + 60
      );

      // Registered By (right) — who created the sample
      const registeredByName =
        data?.createdBy?.fullName ||
        data?.createdBy?.firstName ||
        "N/A";
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const registeredByLabel = "Registered By:";
      const registeredByLabelWidth = doc.getTextWidth(registeredByLabel);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const registeredByValue = ` ${registeredByName}`;
      const registeredByValueWidth = doc.getTextWidth(registeredByValue);
      const registeredByStartX =
        pageWidth - 25 - registeredByLabelWidth - registeredByValueWidth;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(registeredByLabel, registeredByStartX, footerStartY + 60);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(
        registeredByValue,
        registeredByStartX + registeredByLabelWidth,
        footerStartY + 60
      );

      // Page number
      doc.setFont("helvetica", "bold");
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 15, {
        align: "center",
      });
    }
    // Print handling
const blob = doc.output("blob");
const blobUrl = URL.createObjectURL(blob);

// Open PDF in new tab
const newTab = window.open(blobUrl, "_blank");
    };

    // Trigger PDF generation when component mounts or when data/print change
    React.useEffect(() => {
      if (data) {
        generatePDF().catch(err => {
          console.error("Error generating PDF:", err);
        });
      }
    }, [data, print]);

return <></>
};
export default ReportPDF;
